import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Plus, Search, X, Download, Trash2, UserPlus, ChevronDown, FileText, FileSpreadsheet } from 'lucide-react';
import { clientsApi, casesApi, privacyApi, portalApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonTable } from '../components/Skeleton';
import { useTour } from '../hooks/useTour';
import { clientsTourSteps } from '../tours/clientsTour';
import './Pages.css';

const COACHING_TOPICS = [
  'Führungskräfteentwicklung',
  'Berufliche Neuorientierung',
  'Work-Life-Balance',
  'Konfliktmanagement',
  'Stressmanagement & Burnout-Prävention',
  'Karriereplanung',
  'Teamführung & Teamentwicklung',
  'Kommunikation & Auftreten',
  'Persönlichkeitsentwicklung',
  'Selbstmanagement & Zeitmanagement',
  'Entscheidungsfindung',
  'Resilienz',
  'Onboarding / Neue Rolle',
  'Unternehmensgründung',
];

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  status: string;
  profession: string | null;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  active: 'Aktiv',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  archived: 'Archiviert',
};

export default function Clients() {
  useTour('clients', clientsTourSteps);
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const res = await clientsApi.list({ search: search || undefined });
      setClients(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (searchParams.get('neu') === '1') setShowForm(true);
  }, []); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line

  return (
    <div>
      <div className="flex-between mb-24">
        <div>
          <h1>Klienten</h1>
          <p className="text-muted text-sm">{total} Klienten insgesamt</p>
        </div>
        <div className="flex gap-8">
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary" data-tour="client-export" onClick={() => setShowExport(!showExport)}>
              <Download size={16} /> Exportieren <ChevronDown size={14} />
            </button>
            {showExport && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: 4,
                background: 'var(--bg-card)', border: '1px solid #e0ddd8', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: 180,
              }}>
                <button
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.85rem' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  onClick={async () => {
                    setShowExport(false);
                    try {
                      const res = await clientsApi.exportCsv();
                      const blob = new Blob([res.data], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Klienten_${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { showToast('CSV-Export fehlgeschlagen', 'error'); }
                  }}
                >
                  <FileSpreadsheet size={16} /> Als CSV
                </button>
                <button
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.85rem' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  onClick={async () => {
                    setShowExport(false);
                    try {
                      const res = await clientsApi.exportPdf();
                      const blob = new Blob([res.data], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Klienten_${new Date().toISOString().slice(0, 10)}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { showToast('PDF-Export fehlgeschlagen', 'error'); }
                  }}
                >
                  <FileText size={16} /> Als PDF
                </button>
              </div>
            )}
          </div>
          <button className="btn btn-primary" data-tour="client-add-btn" onClick={() => setShowForm(true)}>
            <Plus size={18} /> Neuer Klient
          </button>
        </div>
      </div>

      <div className="search-bar mb-16" data-tour="client-search">
        <Search size={18} className="search-icon" />
        <input
          type="text"
          placeholder="Klienten suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="card" style={{ padding: 16 }}>
          <SkeletonTable />
        </div>
      ) : clients.length === 0 ? (
        <div className="empty-state card">
          <p>Noch keine Klienten vorhanden.</p>
          <button className="btn btn-primary mt-16" onClick={() => setShowForm(true)}>
            <Plus size={18} /> Ersten Klienten anlegen
          </button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table" data-tour="client-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Telefon</th>
                <th>Beruf</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>
                    <Link to={`/klienten/${c.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {c.first_name} {c.last_name}
                    </Link>
                  </td>
                  <td className="text-light">{c.email || '—'}</td>
                  <td className="text-light">{c.phone || '—'}</td>
                  <td className="text-light">{c.profession || '—'}</td>
                  <td>
                    <span className={`badge badge-${c.status}`}>
                      {statusLabels[c.status] || c.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-8">
                      <button className="btn btn-sm btn-accent" title="Portal-Einladung senden" onClick={async () => {
                        try {
                          const res = await portalApi.invite({ client_id: c.id });
                          showToast(res.data.message, 'success');
                        } catch (err: any) {
                          const detail = err.response?.data?.detail;
                          showToast(typeof detail === 'string' ? detail : 'Einladung fehlgeschlagen', 'error');
                        }
                      }}>
                        <UserPlus size={14} />
                        <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>Portal</span>
                      </button>
                      <button className="btn btn-sm btn-secondary" title="Daten exportieren (DSGVO)" onClick={async () => {
                        try {
                          const res = await privacyApi.exportClientData(c.id);
                          const blob = new Blob([res.data], { type: 'application/pdf' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `Klient_${c.last_name}_Export.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch { showToast('Export fehlgeschlagen', 'error'); }
                      }}>
                        <Download size={14} />
                      </button>
                      <button className="btn btn-sm btn-danger" title="Klient löschen (DSGVO)" onClick={async () => {
                        if (!window.confirm(`${c.first_name} ${c.last_name} wirklich löschen? Rechnungen werden anonymisiert (gesetzliche Aufbewahrungspflicht).`)) return;
                        try {
                          await privacyApi.deleteClient(c.id);
                          load();
                        } catch { showToast('Löschen fehlgeschlagen', 'error'); }
                      }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <ClientForm onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

function CoachingTopicField({ value, onChange, label }: { value: string; onChange: (val: string) => void; label?: string }) {
  const [isCustom, setIsCustom] = useState(!COACHING_TOPICS.includes(value) && value !== '');

  return (
    <div className="form-group">
      <label>{label || 'Coaching-Thema *'}</label>
      <select
        value={isCustom ? '__custom__' : value}
        onChange={(e) => {
          if (e.target.value === '__custom__') {
            setIsCustom(true);
            onChange('');
          } else {
            setIsCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">— Thema wählen —</option>
        {COACHING_TOPICS.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
        <option value="__custom__">Eigenes Thema eingeben...</option>
      </select>
      {isCustom && (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Eigenes Coaching-Thema eingeben..."
          required
          autoFocus
          style={{ marginTop: 8 }}
        />
      )}
      <span className="text-muted" style={{ fontSize: '0.75rem', marginTop: 4, display: 'block' }}>
        Es wird automatisch ein Fall mit diesem Thema angelegt.
      </span>
    </div>
  );
}

function ClientForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    profession: '',
    coaching_topic: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      // 1. Klient anlegen
      const res = await clientsApi.create({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        profession: form.profession || undefined,
        notes: form.notes || undefined,
      });
      const newClientId = res.data.id;

      // 2. Automatisch einen Fall (Coaching-Prozess) erstellen
      const topic = form.coaching_topic.trim()
        || `Coaching ${form.first_name} ${form.last_name}`;
      await casesApi.create({
        title: topic,
        case_type: 'individual',
        client_id: newClientId,
      });

      onSaved();
      onClose();

      // 3. Direkt zur Klienten-Detailseite (Prozessmappe) navigieren
      navigate(`/klienten/${newClientId}?tab=prozess`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Neuer Klient</h2>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Vorname *</label>
              <input name="first_name" value={form.first_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Nachname *</label>
              <input name="last_name" value={form.last_name} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>E-Mail</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Beruf</label>
            <input name="profession" value={form.profession} onChange={handleChange} />
          </div>
          <CoachingTopicField
            value={form.coaching_topic}
            onChange={(val) => setForm({ ...form, coaching_topic: val })}
          />
          <div className="form-group">
            <label>Notizen</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : 'Klient anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
